import React, {useEffect, useState} from "react";
import { useTheme } from "react-jss";
import { useLocation, useNavigate } from 'react-router-dom';
import { FormattedMessage } from "react-intl";

/* Helpers, constants, custom methods, internal libraries */
import { usePolicyService } from '../../services/policyService.js';
import { assignTypes } from "../../constants/appConstants.js";
import { useGlobalContext } from "contexts/globalContext.js";
import { useGlobalUpdateContext } from "contexts/globalContext.js";

/* Components */
import PolicyPageHeader from "../../components/policyPageHeader";
import PolicyGrid from "../../components/policy/policyGrid";
import AssignModal from "components/modals/assignModal/index.js";
import ConfirmAssignModal from "../../components/modals/confirmAssignModal"
import { ReactComponent as ClipboardIcon} from "../../resources/images/clipboard-check.svg"

/* Styles */
import styles from "./styles.js"


const Policy = () => {
  const theme = useTheme();
  const classes = styles({ theme });

  const location = useLocation();
  const navigate = useNavigate();
  const globalProperties  = useGlobalContext();
  const setGlobalProperties = useGlobalUpdateContext();
  const [showConfirmAssignModal, setShowConfirmAssignModal] = useState(false);
  const [assignData, setAssignData] = useState({});
  const [initialGroupData, setInitialGroupData] = useState({});

  const { policyResponse, getPolicyList, deletePolicy, getAssignTeamsByPolicyData, assignPolicy } = usePolicyService();

  const [showAssignModal, setShowAssignModal] = useState(false);

  const [gridOptions, setGridOptions] = useState(() => {
    const initialPage = location.pathname.includes('/policy')
      ? new URLSearchParams(location.search).get('page') || 1
      : 1;
    return {
      page: +initialPage,
      filters: {
        STATUS: [],
      },
    };
  });

  useEffect(() => {
    if (globalProperties?.addFirstPolicyData?.firstTimeSaved) {
      displayShowAssignModal(globalProperties.addFirstPolicyData)
      setAssignData({
        ...assignData,
        row: globalProperties.addFirstPolicyData
      })
    }
  }, [])

  useEffect(() => {
    if(!showAssignModal) {
      setGlobalProperties({
        ...globalProperties,
        addFirstPolicyData: {
          ...globalProperties.addFirstPolicyData,
          firstTimeSaved: false
        }
      })
    }
  }, [showAssignModal])

  useEffect(() => {
    getPolicyList(gridOptions);
    gridOptions.page > 1 ? navigate(`/app/policy?page=${gridOptions.page}`) : navigate(`/app/policy`)
  }, [gridOptions]);

  useEffect(() => {
    const pageFromURL = location.pathname.includes('/policy')
      ? new URLSearchParams(location.search).get('page') || 1
      : 1;
    if (+pageFromURL !== gridOptions.page) {
      setGridOptions({ ...gridOptions, page: pageFromURL });
    }
  }, [location.search]);

  const handleDeletePolicy = async (policyId) => {
    const isLastPolicyOnPage = policyResponse?.results?.dataItems?.length === 1;

    const response = await deletePolicy(policyId);

    if (!response) return false;
    
    const nextPage =
      isLastPolicyOnPage && gridOptions.page > 1
        ? gridOptions.page - 1
        : gridOptions.page;

    setGridOptions(prev => ({
      ...prev,
      page: nextPage
    }));
    
    return true;
  };

  const handleAssignPolicy = () => {
    setShowAssignModal(false)
    setShowConfirmAssignModal(true)
  }

  const handleConfirmAssignPolicy = async () => {
    const teamIdsToAssign = assignData.response.teams.filter(team => team.policyId === assignData.row.policyId).map(team => team.entityId)
    const policyData = {
      reqBody: {
        ids: teamIdsToAssign
      },
      policyId: assignData.row.policyId,
      policyName: assignData.row.policyName
    }

    const response = await assignPolicy(policyData)
    if (response) {
      getPolicyList(gridOptions);
      setShowConfirmAssignModal(false);
    }
  }

  const handleCancelConfirm = () => {
    setShowConfirmAssignModal(false)
    setShowAssignModal(true)
  }

  const displayShowAssignModal = async (row) => {
    const response = await getAssignTeamsByPolicyData(`assignTeamsPolicyButton${row.policyId}`);
    if(response) {
      setAssignData({
        response: response,
        row: row
      })
      setInitialGroupData(JSON.parse(JSON.stringify(response.teams || [])));
      setShowAssignModal(true)
    }
  }

  return (
    <div className={classes.policyPage}>
      <PolicyPageHeader />
      <PolicyGrid 
        policyResponse={policyResponse}
        gridOptions={gridOptions}
        setGridOptions={setGridOptions}
        displayShowAssignModal={displayShowAssignModal}
        deletePolicy={handleDeletePolicy}
      />
      <AssignModal 
        showModal={showAssignModal}
        setShowModal={setShowAssignModal}
        type={assignTypes.policy}
        handleAssign={handleAssignPolicy}
        modalIcon={ <ClipboardIcon /> }
        modalTitle={ assignData?.row?.policyName }
        modalDescription={ <FormattedMessage id="policy.assignModal.description" /> }
        assignData={assignData}
        setAssignData={setAssignData}
        confirmButtonText={<FormattedMessage id="policy.assignModal.btnText" />}
        initialGroupData={initialGroupData}
      />
      <ConfirmAssignModal 
        showModal={showConfirmAssignModal}
        setShowModal={setShowConfirmAssignModal}
        handleConfirm={handleConfirmAssignPolicy}
        handleCancelConfirm={handleCancelConfirm}
        modalTitle={ <FormattedMessage id="policy.confirmAssignModal.title" /> }
        modalDescription={ <FormattedMessage id="policy.confirmAssignModal.description" />}
        modalContent= { <FormattedMessage id="policy.confirmAssignModal.content" /> }
        modalIcon={ <ClipboardIcon /> }
      />
    </div>
  )
}

export default Policy;